import React from 'react';
import type { ClassNameProp } from '../common-types';
import { Button } from './Button';
import { Icon } from './Icon';
import { Breakpoints, intl, useBreakpoint } from '../../lib';
import { Modal } from './Modal';
import { isEmptyString } from '../../util';
import { uniqueId } from 'lodash';
import { HBox } from '../layout';
import { LoadMask } from './LoadMask';

export interface SuccessModalProps extends ClassNameProp {
  /**
   * Action to close SuccessModal
   */
  onClick: () => void;
  /**
   * Title of SuccessModal.
   */
  title: string;
  /**
   * Description of SuccessModal.
   */
  description: React.JSX.Element | string;
  /**
   * Title of the pre confirmation task list.
   */
  preConfTaskListTitle?: string;
  /**
   * Description of the pre confirmation task list.
   */
  preConfTaskListDescription?: React.JSX.Element | string;
  /**
   * Pre confirmation task list.
   */
  tasksTitles?: string[];
  /**
   * Text of confirmation button.
   */
  buttonText?: string;
  /**
   * Set breakpoint for Mobile View.
   */
  mobileBreakpoint?: number;
  /**
   * Set if it is success modal should render inside element.
   */
  renderInsideElement?: boolean;
  /**
   * Show a busy indicator instead of the checkmark and button to indicate that something is loading.
   */
  loading?: boolean;
}

/**
 * Success Modal component for patients with both Desktop and MobileView.
 *
 * @param properties a {@link SuccessModalProps} object.
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function SuccessModal({
  className,
  onClick,
  title,
  description,
  preConfTaskListTitle,
  preConfTaskListDescription,
  tasksTitles,
  buttonText,
  mobileBreakpoint = Breakpoints.MOBILE,
  renderInsideElement,
  loading,
  ...props
}: Readonly<SuccessModalProps>): React.JSX.Element {
  const isMobileView = useBreakpoint(mobileBreakpoint);
  const isTasksTitles = !!(tasksTitles && tasksTitles.length > 0);
  const taskClassName = isTasksTitles ? 'pre-conf-tasks' : '';
  const successButton = (
    <div className={`success-button ${loading ? ' invisible' : ''}`.trim()}>
      <Button
        variant={'primary'}
        text={
          buttonText && !isEmptyString(buttonText)
            ? buttonText
            : intl('common.continue')
        }
        scale={'medium'}
        className="success-continue"
        onClick={onClick}
      />
    </div>
  );
  const successfulMessage = (
    <SuccessfulMessage
      loading={loading}
      className={className ?? ''}
      title={title}
      description={description}
      preConfTaskListTitle={preConfTaskListTitle}
      preConfTaskListDescription={preConfTaskListDescription}
      tasksTitles={tasksTitles}
      successButton={isMobileView ? successButton : undefined}
    ></SuccessfulMessage>
  );

  let content;

  if (isMobileView) {
    content = (
      <div {...props} className={`ehs-success-modal mobile ${taskClassName}`}>
        {successfulMessage}
      </div>
    );
  } else if (renderInsideElement) {
    content = (
      <div
        {...props}
        className={`ehs-success-modal inside-element ${taskClassName}`}
      >
        {successfulMessage}
        {successButton}
      </div>
    );
  } else {
    content = (
      <Modal
        {...props}
        draggable={false}
        className="ehs-success-modal desktop"
        actions={successButton}
      >
        {successfulMessage}
      </Modal>
    );
  }

  return content;
}

const SuccessfulMessage = ({
  className,
  title,
  description,
  preConfTaskListTitle,
  preConfTaskListDescription,
  tasksTitles,
  successButton,
  loading
}: {
  className?: string;
  title: string;
  description: React.JSX.Element | string;
  preConfTaskListTitle?: string;
  preConfTaskListDescription?: React.JSX.Element | string;
  tasksTitles?: string[];
  successButton: React.JSX.Element | undefined;
  loading?: boolean;
}): React.JSX.Element => {
  const uniqueIdPrefix = uniqueId('ehs-success-');
  const cName = `ehs-success-message ${className ?? ''}`;
  const tasksExists = tasksTitles && tasksTitles.length > 0;

  return (
    <div
      className={cName}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${uniqueIdPrefix}-title`}
      aria-describedby={`${uniqueIdPrefix}-description`}
      tabIndex={tasksExists ? 0 : undefined}
    >
      <div className="icon-container">
        {loading ? (
          <LoadMask masked={true} />
        ) : (
          <Icon aria-label={''} iconName="check" className="fade-in" />
        )}
      </div>
      <div className="success-text" id="success-text">
        <h2
          id={`${uniqueIdPrefix}-title`}
          className="success-title sh-font font-22"
        >
          {title}
        </h2>
        <p
          id={`${uniqueIdPrefix}-description`}
          className="success-description s-font"
        >
          {description}
        </p>
      </div>
      {tasksExists && (
        <div className="border-preconftasklist">
          <div className="success-preconftasklist" id="success-task-list">
            <HBox>
              <Icon
                aria-label={undefined}
                iconName={'materialicon-error'}
                className={'icon'}
              />
              <h2
                id={`${uniqueIdPrefix}-pre-confirmation-task-list-title`}
                className="success-title s-font font-16"
              >
                {preConfTaskListTitle}
              </h2>
            </HBox>
            <p
              id={`${uniqueIdPrefix}-pre-confirmation-task-list-description`}
              className="success-preconftasklist-description s-font font-16"
            >
              {preConfTaskListDescription}
            </p>
            <ul>
              {tasksTitles?.map((taskTitle, index) => (
                <li key={taskTitle + index} className={'s-font font-16'}>
                  {taskTitle}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {successButton}
    </div>
  );
};
